import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Signal,
  inject,
  input,
  output,
  viewChildren,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { isPlatformBrowser } from '@angular/common';
import { toObservable } from '@angular/core/rxjs-interop';

import {
  BehaviorSubject,
  Observable,
  Subscription,
  combineLatest,
  withLatestFrom,
  map,
  distinctUntilChanged,
} from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { TripType } from '@fcom/core/interfaces/triptype.interface';
import { stopPropagation } from '@fcom/core/utils/event';
import { CmsTripTypeMap } from '@fcom/core/utils/tripType.utils';
import { ButtonTheme, RadioButtonTheme, PopoverOptions, PopoverService } from '@fcom/ui-components';
import { MonitoringLogger } from '@fcom/core';

import { SelectorType } from '../../interfaces/booking-widget.interface';
import { BookingWidgetService } from '../../services/booking-widget.service';
import { BookingWidgetTripService } from '../../services/booking-widget-trip.service';
import { defaultWidgetPopoverOptions } from '../../constants';
import { BookingWidgetGtmService } from '../../services/booking-widget-gtm.service';

@Component({
  selector: 'fin-trip-type-selector',
  styleUrls: ['./trip-type-selector.component.scss'],
  templateUrl: './trip-type-selector.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TripTypeSelectorComponent implements OnInit, AfterViewInit, OnDestroy {
  private popoverService = inject(PopoverService);
  private bookingWidgetService = inject(BookingWidgetService);
  private bookingWidgetTripService = inject(BookingWidgetTripService);
  private monitoringLogger = inject(MonitoringLogger);
  private bookingWidgetGtmService = inject(BookingWidgetGtmService);
  private platform = inject(PLATFORM_ID);

  readonly disabled = input(false);

  readonly returnDateMissing = input(false);

  readonly isSearchEnabled = input(false);

  readonly isGlobalBookingWidget = input(false);

  readonly setTripType = output<TripType>();

  readonly startSearch = output<void>();

  readonly openCalendar = output<void>();

  readonly selectedTripType$: Observable<TripType>;
  readonly availableTripTypes$: Observable<TripType[]>;
  readonly FORM_CONTROL_NAME = 'tripType';
  readonly RadioButtonTheme = RadioButtonTheme;
  readonly ButtonTheme = ButtonTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly TripType = TripType;
  readonly CmsTripTypeMap = CmsTripTypeMap;
  readonly popoverOptions: PopoverOptions = {
    ...defaultWidgetPopoverOptions,
    popoverID: 'tripTypeSelectorPopover',
    alignToLeft: true,
    disableAutoFocus: true,
  };
  readonly SelectorType = SelectorType;
  addingReturnDate = false;
  private tripTypeCommitted = false;

  onAddReturnDate(): void {
    this.addingReturnDate = true;
    this.openCalendar.emit();
    this.closePopover();
  }

  modalOpen = false;
  selectedElement: ElementRef = null;
  currentTripType$ = new BehaviorSubject<TripType>(TripType.RETURN);
  selectedTripTypeIndex$ = new BehaviorSubject<number>(0);
  subscription = new Subscription();
  popOverFormGroup: FormGroup = new FormGroup({
    [this.FORM_CONTROL_NAME]: new FormControl(),
  });
  tripTypeOptions$: Observable<Array<{ value: TripType; label: string }>>;
  isBrowser = false;

  readonly tripTypeRadioBtn: Signal<readonly ElementRef[]> = viewChildren('tripTypeRadioBtn', { read: ElementRef });

  constructor() {
    this.selectedTripType$ = toObservable(this.bookingWidgetTripService.selectedTripType);
    this.availableTripTypes$ = toObservable(this.bookingWidgetTripService.availableTripTypes).pipe(
      map((tripTypes) => (this.isGlobalBookingWidget() ? tripTypes.filter((t) => t !== TripType.MULTICITY) : tripTypes))
    );
  }

  ngOnInit(): void {
    /**
     * This check is used to only render the multi-city help content popover on the client. The
     * reason is currently unknown but we get an angular hydration error if you try to remove it.
     */
    this.isBrowser = isPlatformBrowser(this.platform);

    this.tripTypeOptions$ = this.availableTripTypes$.pipe(
      map((tripTypes) =>
        tripTypes
          .filter((tripType) => {
            if (!CmsTripTypeMap[tripType]) {
              this.monitoringLogger.warn('TripType is not defined in CmsTripTypeMap', { tripType });
            }
            return !!CmsTripTypeMap[tripType];
          })
          .map((tripType) => ({
            value: tripType,
            label: `travelType.${CmsTripTypeMap[tripType]}`,
          }))
      )
    );

    this.subscription.add(
      // combineLatest, not withLatestFrom: both sources are toObservable() streams whose first
      // values arrive in the same deferred effect flush. withLatestFrom would drop the first
      // selectedTripType$ emission — which is the only one a component instance created after a
      // layout switch (e.g. to multi-city) ever receives, leaving the label stuck at its default.
      combineLatest([this.selectedTripType$, this.availableTripTypes$]).subscribe(([tripType, availableTripTypes]) => {
        const selectedIndex = availableTripTypes.findIndex((t) => t === tripType);
        this.selectedTripTypeIndex$.next(selectedIndex);
        this.currentTripType$.next(availableTripTypes[selectedIndex]);
      })
    );

    if (this.bookingWidgetService.usePopoverSelectors()) {
      this.subscription.add(
        this.selectedTripType$.subscribe((tripType) => {
          this.popOverFormGroup.controls[this.FORM_CONTROL_NAME].setValue(tripType, { emitEvent: false });
        })
      );
      this.subscription.add(
        this.popOverFormGroup
          .get(this.FORM_CONTROL_NAME)
          .valueChanges.pipe(distinctUntilChanged())
          .subscribe((value) => {
            this.setTripType.emit(value);
          })
      );
    }
  }

  ngAfterViewInit(): void {
    this.subscription.add(
      this.selectedTripTypeIndex$
        .pipe(withLatestFrom(this.availableTripTypes$))
        .subscribe(([index, availableTripTypes]) => {
          this.currentTripType$.next(availableTripTypes[index]);
          this.tripTypeRadioBtn()[index]?.nativeElement?.focus();
        })
    );
  }

  handleKeyPress(selectedTripType: TripType, event: KeyboardEvent): void {
    stopPropagation(event);
    const currentIndex = this.selectedTripTypeIndex$.getValue();
    const maxIndex = this.tripTypeRadioBtn().length - 1;

    switch (event.key) {
      case 'Down':
      case 'ArrowDown':
      case 'Right':
      case 'ArrowRight':
        this.selectedTripTypeIndex$.next(currentIndex + 1 > maxIndex ? 0 : currentIndex + 1);
        break;
      case 'Up':
      case 'ArrowUp':
      case 'Left':
      case 'ArrowLeft':
        this.selectedTripTypeIndex$.next(currentIndex - 1 < 0 ? maxIndex : currentIndex - 1);
        break;
      case 'Enter':
      case ' ':
        this.selectTripType(selectedTripType);
        break;
      default:
        break;
    }
  }

  openModal(): void {
    if (this.bookingWidgetService.usePopoverSelectors()) {
      return;
    }

    this.modalOpen = true;
  }

  cancelSelection(): void {
    const originalSelection = this.bookingWidgetService.currentSearch();
    this.bookingWidgetService.setTripType(originalSelection.tripType);
    this.bookingWidgetService.setTravelDates(
      {
        departureDate: originalSelection.flights[0]?.departureDate,
        returnDate: originalSelection.flights[1]?.departureDate,
      },
      0
    );
    this.popoverService.close(true);
    this.bookingWidgetGtmService.trackElementEvent('global-widget-cancel-search-from-trip-type');
  }

  selectTripType(selectedTripType: TripType): void {
    this.modalOpen = false;
    // Sync immediately: the toObservable() chain feeding currentTripType$ only emits on the next
    // effect flush, and confirmOnClose() runs before that when the modal closes.
    this.currentTripType$.next(selectedTripType);
    this.tripTypeCommitted = true;
    this.setTripType.emit(selectedTripType);
  }

  closePopover(): void {
    this.popoverService.close(true);
  }

  onClose(): void {
    if (this.isGlobalBookingWidget()) {
      if (!this.addingReturnDate) {
        this.cancelSelection();
      }
      this.addingReturnDate = false;
    }
  }

  startNewSearch(): void {
    this.startSearch.emit();
    this.bookingWidgetGtmService.trackElementEvent('global-widget-start-new-search-from-trip-type');
  }

  confirmOnClose(): void {
    // Commits the option focused via arrow keys when the modal closes without an explicit selection.
    // Reads synchronously: selectedTripType$/toObservable emissions are deferred to the next effect
    // flush, so subscribing here would compare stale values and revert a selection just made.
    if (this.tripTypeCommitted) {
      this.tripTypeCommitted = false;
      return;
    }
    const currentTripType = this.currentTripType$.getValue();
    if (this.bookingWidgetTripService.selectedTripType() !== currentTripType) {
      this.setTripType.emit(currentTripType);
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.subscription = null;
  }
}
